import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { selectCurrentUser } from 'redux/reducers/auth';
import { useAppSelector } from 'redux/store';
import { useTranslation } from 'react-i18next';
import {
  LumenSidebar,
  LumenSidebarSection,
  LumenSidebarNavItem,
  LumenSidebarSeparator,
} from '@photoshelter/socialie-components';
import routes from 'helpers/routes';
import routeFor from 'helpers/routeFor';
import useCreateUgcRequest from 'hooks/useCreateUgcRequests';
import UserMenu from '../Layout/components/UserMenu';
import AccountsMenu from '../Layout/components/AccountsMenu';
import OverLimitWarning from '../Layout/components/OverLimitWarning';

function GlobalSidebarNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const { handleCreate, isLoading } = useCreateUgcRequest();

  const currentUser = useAppSelector(selectCurrentUser);

  if (!currentUser) return null;

  return (
    <LumenSidebar
      accountMenu={<AccountsMenu currentUser={currentUser} />}
      externalLinks={[
        {
          title: t('globalMenu.distribution'),
          href: 'https://app.socialie.com',
          icon: 'share-external',
        },
        {
          title: t('globalMenu.assetManagement'),
          href: 'https://library.photoshelter.com/library',
          icon: 'share-external',
        },
      ]}
      userMenu={{
        component: <UserMenu currentUser={currentUser} />,
        hoverText: t('globalMenu.userMenu.account'),
        currentUser,
      }}
      menuToast={<OverLimitWarning />}
      upsellPromo={{
        title: t('globalMenu.promo.title'),
        body: t('globalMenu.promo.body'),
      }}
    >
      <LumenSidebarSection title={t('globalMenu.UGC')}>
        <LumenSidebarNavItem
          text={t('globalMenu.createRequest')}
          icon="plus"
          href="#"
          isActive={location.pathname.includes('/requests/draft/')}
          isDisabled={isLoading}
          onClick={handleCreate}
          renderLink={RenderLink}
        />

        <LumenSidebarSeparator />

        <LumenSidebarNavItem
          text={t('globalMenu.request')}
          icon="request"
          href={routeFor(routes.requests.index, 'live')}
          isActive={location.pathname.startsWith(
            routeFor(routes.requests.index, 'live')
          )}
          renderLink={RenderLink}
        />

        <LumenSidebarNavItem
          text={t('settings.nav.brand')}
          icon="branding"
          href={routes.settings.brand}
          isActive={location.pathname.startsWith(routes.settings.brand)}
          renderLink={RenderLink}
        />

        <LumenSidebarNavItem
          text={t('settings.nav.users')}
          icon="groups"
          href={routes.settings.users}
          isActive={location.pathname.startsWith(routes.settings.users)}
          renderLink={RenderLink}
        />

        <LumenSidebarNavItem
          text={t('settings.nav.domains')}
          icon="domain"
          href={routes.settings.domains.root}
          isActive={location.pathname.startsWith(routes.settings.domains.root)}
          renderLink={RenderLink}
        />

        <LumenSidebarNavItem
          text={t('settings.nav.subscription')}
          icon="subscription"
          href={routes.settings.subscription}
          isActive={location.pathname.startsWith(routes.settings.subscription)}
          renderLink={RenderLink}
        />
      </LumenSidebarSection>
    </LumenSidebar>
  );
}

interface LinkRenderProps {
  href: string;
  children: React.ReactNode;
  [key: string]: any;
}

const RenderLink = ({ href, children, ...linkProps }: LinkRenderProps) => {
  return (
    <Link to={href} {...linkProps}>
      {children}
    </Link>
  );
};

export default GlobalSidebarNav;
